import type { ReactNode } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatINR } from '@/lib/format';
import { enqLineAmount, type Enquiry } from './enquiryDomain';

interface Props {
  open: boolean;
  row: Enquiry | null;
  onClose: () => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}

/** Read-only detail view of a single enquiry — items + communication log. */
export function EnquiryViewModal({ open, row, onClose }: Props) {
  if (!row) return null;
  const comms = [...(row.comms ?? [])].sort((a, b) => b.date.localeCompare(a.date));
  const total = row.items.reduce((s, l) => s + enqLineAmount(l), 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${row.customer} — Enquiry details${row.enqNo ? ` · ${row.enqNo}` : ''}`}
      size="lg"
      footer={<Button variant="glass" onClick={onClose}>Close</Button>}
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Date">{row.date}</Field>
        <Field label="Category">{row.category ? <Badge tone="accent">{row.category}</Badge> : '—'}</Field>
        <Field label="Status"><Badge tone="info">{row.status}</Badge></Field>
        <Field label="Channel">{row.channel || '—'}</Field>
        <Field label="Assigned To">{row.assignedTo || '—'}</Field>
        <Field label="Approved By">{row.approvedBy || '—'}</Field>
        <Field label="Sales Order No">{row.salesOrderNo || '—'}</Field>
        <Field label="Order Value">{row.valueInr ? formatINR(row.valueInr) : '—'}</Field>
      </div>

      {row.items.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-[var(--border-color)]">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                {['Item', 'Qty', 'Unit', 'Rate', 'Amount'].map((h, i) => (
                  <th key={h} className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-wide text-[var(--text-muted)]" style={{ textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {row.items.map((it, i) => (
                <tr key={it.id} style={{ borderTop: i > 0 ? '1px solid var(--border-color)' : 'none' }}>
                  <td className="px-3 py-2 text-[var(--text-primary)]">{it.item}</td>
                  <td className="px-3 py-2 text-right">{it.qty}</td>
                  <td className="px-3 py-2 text-right">{it.unit}</td>
                  <td className="px-3 py-2 text-right">{it.rate ? formatINR(it.rate) : '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-accent">{formatINR(enqLineAmount(it))}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                <td colSpan={4} className="px-3 py-2 text-right text-xs font-bold text-[var(--text-secondary)]">Est. Order Value</td>
                <td className="px-3 py-2 text-right text-sm font-extrabold text-accent">{formatINR(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {row.notes && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Notes</div>
          <div className="mt-1 whitespace-pre-wrap rounded-lg border border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text-secondary)]">
            {row.notes}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 text-xs text-[var(--text-muted)]">
          Communication: <strong className="text-[var(--text-primary)]">{comms.length} logged</strong>
        </div>
        <div className="max-h-56 overflow-y-auto rounded-lg border border-[var(--border-color)]">
          {comms.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">No communication logged yet.</div>
          ) : (
            comms.map((c, i) => (
              <div key={c.id} className="px-4 py-2.5" style={{ borderBottom: i < comms.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                <div className="mb-1 flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                  <Badge tone="info">{c.type}</Badge>
                  <span>{c.date}{c.by ? ` · ${c.by}` : ''}</span>
                </div>
                {c.note && <div className="text-sm text-[var(--text-secondary)]">{c.note}</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
